import React from 'react';
import api from '../services/api';
import { LEAD_SOURCES } from '../constants/leadSources';

// Lead sources from the admin-managed table, with the old hardcoded list as a
// fallback.
//
// Without this the Lead Sources admin page is half-wired: Trey adds "Facebook",
// and it never appears in the job form or the automation condition builder because
// both still read the constant.
//
// Cached at module level rather than per component — the job form and the
// condition builder can both be open at once, and this list changes about twice a
// year. The fallback matters because the dropdown must still work offline or
// against an older API, and because the built-in values are the exact strings four
// live automations match on.
let cache = null;
let inFlight = null;

const fetchOnce = () => {
  if (cache) return Promise.resolve(cache);
  if (inFlight) return inFlight;

  inFlight = api.get('/lead-sources')
    .then((res) => {
      const rows = Array.isArray(res.data) ? res.data : [];
      // An empty table means the seed has not run yet, not "no lead sources" —
      // falling back is right in both cases.
      cache = rows.length
        ? rows.map((r) => ({ value: r.value, label: r.label }))
        : LEAD_SOURCES.map((s) => ({ value: s.value, label: s.label }));
      return cache;
    })
    .catch(() => {
      // Do NOT cache the fallback: a failure here is usually transient, and
      // caching it would keep a newly added source hidden for the whole session.
      return LEAD_SOURCES.map((s) => ({ value: s.value, label: s.label }));
    })
    .finally(() => { inFlight = null; });

  return inFlight;
};

export default function useLeadSources() {
  const [sources, setSources] = React.useState(
    () => cache || LEAD_SOURCES.map((s) => ({ value: s.value, label: s.label }))
  );

  React.useEffect(() => {
    let cancelled = false;
    fetchOnce().then((list) => { if (!cancelled) setSources(list); });
    return () => { cancelled = true; };
  }, []);

  return sources;
}
